import { apiClient } from '@/api/client';

type WebPushConfig = {
  publicKey: string;
  enabled: boolean;
};

export type NotificationSettings = {
  pushEnabled: boolean;
};

export const supportsWebPush = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const isIOSWithoutInstalledPWA = () => {
  const isIOS =
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
  return isIOS && !standalone;
};

const urlBase64ToUint8Array = (value: string) => {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
};

const equalKeys = (left: ArrayBuffer | null, right: Uint8Array) => {
  if (!left) return false;
  const current = new Uint8Array(left);
  return current.length === right.length && current.every((value, index) => value === right[index]);
};

export const getNotificationSettings = () =>
  apiClient.get<NotificationSettings>('/me/notification-settings');

export const setNotificationSettings = (pushEnabled: boolean) =>
  apiClient.patch<NotificationSettings>('/me/notification-settings', { pushEnabled });

export const enablePushNotifications = async () => {
  if (!supportsWebPush()) {
    throw new Error('Этот браузер не поддерживает push-уведомления');
  }
  if (isIOSWithoutInstalledPWA()) {
    throw new Error('На iPhone сначала добавьте приложение на экран «Домой»');
  }

  const config = await apiClient.get<WebPushConfig>('/config/web-push');
  if (!config.enabled || !config.publicKey) {
    throw new Error('Push-уведомления пока не настроены на сервере');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Разрешение на уведомления не предоставлено');
  }

  const registration = await navigator.serviceWorker.ready;
  const applicationServerKey = urlBase64ToUint8Array(config.publicKey.trim());
  let existing = await registration.pushManager.getSubscription();
  if (existing && !equalKeys(existing.options.applicationServerKey, applicationServerKey)) {
    await apiClient.delete<void>('/me/push-subscriptions', {
      data: { endpoint: existing.endpoint },
    }).catch(() => undefined);
    await existing.unsubscribe();
    existing = null;
  }
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    }));

  await apiClient.post<void>('/me/push-subscriptions', subscription.toJSON());
  return setNotificationSettings(true);
};

const favoritePromptKey = 'push_favorite_prompt_shown';

export const promptForPushAfterFavorite = async () => {
  if (
    !supportsWebPush() ||
    Notification.permission !== 'default' ||
    localStorage.getItem(favoritePromptKey)
  ) {
    return;
  }
  localStorage.setItem(favoritePromptKey, 'true');
  if (window.confirm('Получать уведомления о начале матча, изменении счёта и результате?')) {
    try {
      await enablePushNotifications();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Не удалось включить уведомления');
    }
  }
};
